import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, Eye, Lock, RefreshCw, ShieldCheck, Snowflake } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'medium' }) : '—');

const SNAPSHOT_FIELDS = [
  ['fx_rate',            'FX rate'],
  ['fx_pair',            'FX pair'],
  ['commission_pct',     'Comisión %'],
  ['commission_amount',  'Comisión monto'],
  ['provider_cost',      'Costo provider'],
  ['selling_price',      'Precio venta'],
];

const hasSnapshot = (tx) => {
  const md = tx.metadata || {};
  const fx = tx.fx_rate_snapshot || {};
  if (Object.keys(fx).length > 0) return true;
  return SNAPSHOT_FIELDS.some(([k]) => md[k] !== undefined && md[k] !== null);
};

const FinanceSnapshotsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/finance/transactions`, { headers: getAuthHeaders(), params: { limit: 200 } });
      setTransactions(r.data.transactions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const snapshots = useMemo(() => {
    let list = transactions.filter(hasSnapshot);
    if (search) {
      const t = search.toLowerCase();
      list = list.filter((tx) => (
        (tx.id || '').toLowerCase().includes(t) ||
        (tx.kind || '').toLowerCase().includes(t) ||
        (tx.operation_id || '').toLowerCase().includes(t) ||
        JSON.stringify(tx.metadata || {}).toLowerCase().includes(t)
      ));
    }
    return list;
  }, [transactions, search]);

  return (
    <div className="space-y-6" data-testid="finance-snapshots-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Camera className="w-6 h-6 text-indigo-600" />
            Snapshots Financieros
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Valores congelados al momento del asiento contable: FX, comisiones, costo proveedor, precio de venta.
            Nunca se recalculan dinámicamente — se leen tal como fueron sellados.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 gap-1">
            <Lock className="w-3 h-3" /> Read-only
          </Badge>
          <Badge variant="outline" className="bg-cyan-50 border-cyan-200 text-cyan-700 gap-1">
            <Snowflake className="w-3 h-3" /> Frozen
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1">
            <ShieldCheck className="w-3 h-3" /> Inmutable
          </Badge>
          <Button size="sm" variant="outline" onClick={fetchData} data-testid="snapshots-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm p-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por id / kind / operation / metadata…"
          data-testid="snapshots-search-input"
        />
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : snapshots.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Camera className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p>Sin snapshots disponibles</p>
            <p className="text-xs text-slate-400 mt-1">
              Cuando un asiento se publica con FX o costos congelados, aparecerá aquí automáticamente.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-left">
                  <th className="p-3 font-medium text-slate-600">Tx</th>
                  <th className="p-3 font-medium text-slate-600">Kind</th>
                  <th className="p-3 font-medium text-slate-600">Operation</th>
                  <th className="p-3 font-medium text-slate-600">Actor</th>
                  <th className="p-3 font-medium text-slate-600">Fields congelados</th>
                  <th className="p-3 font-medium text-slate-600">Frozen at</th>
                  <th className="p-3 font-medium text-slate-600 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((tx) => {
                  const md = tx.metadata || {};
                  const fx = tx.fx_rate_snapshot || {};
                  const frozenFields = SNAPSHOT_FIELDS.filter(([k]) => md[k] !== undefined && md[k] !== null).map(([, label]) => label);
                  if (Object.keys(fx).length > 0) frozenFields.unshift('FX rates');
                  return (
                    <tr
                      key={tx.id}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => setDetail(tx)}
                      data-testid={`snapshot-row-${tx.id}`}
                    >
                      <td className="p-3 font-mono text-xs text-slate-800">{tx.id.slice(0, 8)}…</td>
                      <td className="p-3 text-slate-700">{tx.kind}</td>
                      <td className="p-3 font-mono text-xs text-slate-500">{tx.operation_id ? `${tx.operation_id.slice(0, 8)}…` : '—'}</td>
                      <td className="p-3 text-xs text-slate-500">{tx.actor_role} · <span className="font-mono">{tx.actor_id?.slice(0, 8)}…</span></td>
                      <td className="p-3 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {frozenFields.map((f) => (
                            <span key={f} className="px-1.5 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded text-[10px]">{f}</span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 text-slate-400 whitespace-nowrap text-xs">{fmtDate(md.frozen_at || tx.posted_at)}</td>
                      <td className="p-3"><Eye className="w-4 h-4 text-slate-400" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="snapshot-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Snowflake className="w-5 h-5 text-cyan-600" />
              Snapshot congelado
            </DialogTitle>
          </DialogHeader>
          {detail && (() => {
            const md = detail.metadata || {};
            const fx = detail.fx_rate_snapshot || {};
            return (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-slate-500">Tx ID</p><p className="font-mono text-xs break-all">{detail.id}</p></div>
                  <div><p className="text-xs text-slate-500">Kind</p><p className="text-slate-800">{detail.kind}</p></div>
                  <div><p className="text-xs text-slate-500">Operation</p><p className="font-mono text-xs">{detail.operation_id || '—'}</p></div>
                  <div><p className="text-xs text-slate-500">Actor</p><p className="text-xs">{detail.actor_role} · <span className="font-mono">{detail.actor_id}</span></p></div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                    <Snowflake className="w-3 h-3 text-cyan-600" /> Valores congelados
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    {SNAPSHOT_FIELDS.map(([k, label]) => (
                      <div key={k} className="flex justify-between px-3 py-2 border-b last:border-0 bg-white">
                        <span className="text-xs text-slate-500">{label}</span>
                        <span className="font-mono text-xs text-slate-800">{md[k] ?? <span className="text-slate-300">—</span>}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {Object.keys(fx).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-2">FX rate snapshot</p>
                    <pre className="font-mono text-[11px] bg-slate-50 p-2 rounded border">{JSON.stringify(fx, null, 2)}</pre>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
                  <div>
                    <p className="font-semibold">Frozen at</p>
                    <p className="font-mono">{fmtDate(md.frozen_at || detail.posted_at)}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Posted at</p>
                    <p className="font-mono">{fmtDate(detail.posted_at)}</p>
                  </div>
                </div>

                {md.extra && (
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-1">Extra</p>
                    <pre className="font-mono text-[11px] bg-slate-50 p-2 rounded border">{JSON.stringify(md.extra, null, 2)}</pre>
                  </div>
                )}

                <p className="text-[11px] text-slate-400 italic flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Estos valores se sellaron al momento del asiento y nunca se recalculan.
                </p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceSnapshotsPage;
